import { PetCustomPrice, PetCustomPriceCreatePayload } from '@spike/pet-model';
import { createPetCustomPriceThunk, fetchPetCustomPricesThunk, updatePetCustomPriceThunk } from '@spike/pets-action';
import { ApiClientWrapper } from '@spike/api-client';
import {
    AppointmentMultiple,
    Boarding,
    DataVaraible,
    Daycare,
    PetServiceWrapper,
    Training,
    Transportation,
    Walking
} from 'model/NewMultipleBooking';
import moment, { Moment } from 'moment-timezone';
import { Dispatch } from 'redux';
import store from 'store';
import { buildTransportationVariableDataPerSlot } from 'utils/transportationBulkSlotBuilder';

export type BookingCustomPriceKind = 'standard' | 'pickup' | 'dropoff';

export function findStandardCustomPrice(
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): PetCustomPrice | undefined {
    return customPrices.find(price => price.serviceId === serviceId && price.price != null);
}

export function findPickupCustomPrice(
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): PetCustomPrice | undefined {
    return customPrices.find(price => price.serviceId === serviceId && price.pickupPrice != null);
}

export function findDropoffCustomPrice(
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): PetCustomPrice | undefined {
    return customPrices.find(price => price.serviceId === serviceId && price.dropoffPrice != null);
}

export function resolveStandardPrice(
    catalogPrice: number,
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): { price: number; customPriceId?: number } {
    const custom = findStandardCustomPrice(customPrices, serviceId);
    if (custom?.price != null) {
        return { price: custom.price, customPriceId: custom.id };
    }
    return { price: catalogPrice };
}

export function resolvePickupPrice(
    catalogPrice: number,
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): { price: number; customPriceId?: number } {
    const custom = findPickupCustomPrice(customPrices, serviceId);
    if (custom?.pickupPrice != null) {
        return { price: custom.pickupPrice, customPriceId: custom.id };
    }
    return { price: catalogPrice };
}

export function resolveDropoffPrice(
    catalogPrice: number,
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): { price: number; customPriceId?: number } {
    const custom = findDropoffCustomPrice(customPrices, serviceId);
    if (custom?.dropoffPrice != null) {
        return { price: custom.dropoffPrice, customPriceId: custom.id };
    }
    return { price: catalogPrice };
}

export function isSameMoney(a?: number | null, b?: number | null): boolean {
    const left = Number(a);
    const right = Number(b);
    if (!Number.isFinite(left) || !Number.isFinite(right)) {
        return false;
    }
    return Math.abs(left - right) < 0.004;
}

interface PersistEntry {
    petId: number;
    serviceId: number;
    price: number;
    kind: BookingCustomPriceKind;
}

function findCustomPriceByServiceId(
    customPrices: Array<PetCustomPrice>,
    serviceId: number
): PetCustomPrice | undefined {
    return customPrices.find(price => price.serviceId === serviceId);
}

function collectPersistEntries(appointments: Array<AppointmentMultiple>): Array<PersistEntry> {
    const byKey = new Map<string, PersistEntry>();

    for (const appointment of appointments) {
        for (const data of appointment.variableData || []) {
            if (!data.service?.saveCustomPrice || !data.service.service?.id) continue;

            const kind: BookingCustomPriceKind =
                data.itemData?.type === 'pickup'
                    ? 'pickup'
                    : data.itemData?.type === 'dropoff'
                    ? 'dropoff'
                    : 'standard';

            const formPrice = Number(data.service.price) || 0;
            const storedAmount = Number(data.service.customPriceAmount);
            const hasStoredAmount = Number.isFinite(storedAmount) && storedAmount > 0;
            const hasMembership = data.service.enrollment?.id != null;
            // Membership slots store the pre-discount form price in customPriceAmount.
            // Without membership, service.price is the edited form price and must win over a stale overlay.
            const persistAmount = hasMembership && hasStoredAmount ? storedAmount : formPrice || storedAmount || 0;

            byKey.set(`${appointment.petId}-${data.service.service.id}-${kind}`, {
                petId: appointment.petId,
                serviceId: data.service.service.id,
                price: persistAmount,
                kind
            });
        }
    }

    return Array.from(byKey.values());
}

async function persistPickupAndDropoff(
    dispatch: Dispatch<any>,
    apiClientWrapper: ApiClientWrapper,
    petId: number,
    serviceId: number,
    pickupPrice: number,
    dropoffPrice: number,
    customPrices: Array<PetCustomPrice>
): Promise<void> {
    const existing = findCustomPriceByServiceId(customPrices, serviceId);
    if (existing) {
        await dispatch(
            updatePetCustomPriceThunk(apiClientWrapper, petId, existing.id, {
                pickupPrice,
                dropoffPrice
            })
        );
        return;
    }

    const payload: PetCustomPriceCreatePayload = {
        serviceId,
        pickupPrice,
        dropoffPrice
    };
    await dispatch(createPetCustomPriceThunk(apiClientWrapper, petId, payload));
}

async function persistSingleCustomPrice(
    dispatch: Dispatch<any>,
    apiClientWrapper: ApiClientWrapper,
    entry: PersistEntry,
    customPrices: Array<PetCustomPrice>
): Promise<void> {
    if (entry.kind === 'pickup') {
        const existing = findCustomPriceByServiceId(customPrices, entry.serviceId);
        if (existing) {
            await dispatch(
                updatePetCustomPriceThunk(apiClientWrapper, entry.petId, existing.id, {
                    pickupPrice: entry.price
                })
            );
        } else {
            const payload: PetCustomPriceCreatePayload = {
                serviceId: entry.serviceId,
                pickupPrice: entry.price
            };
            await dispatch(createPetCustomPriceThunk(apiClientWrapper, entry.petId, payload));
        }
        return;
    }

    if (entry.kind === 'dropoff') {
        const existing = findCustomPriceByServiceId(customPrices, entry.serviceId);
        if (existing) {
            await dispatch(
                updatePetCustomPriceThunk(apiClientWrapper, entry.petId, existing.id, {
                    dropoffPrice: entry.price
                })
            );
        } else {
            const payload: PetCustomPriceCreatePayload = {
                serviceId: entry.serviceId,
                dropoffPrice: entry.price
            };
            await dispatch(createPetCustomPriceThunk(apiClientWrapper, entry.petId, payload));
        }
        return;
    }

    const existing = findStandardCustomPrice(customPrices, entry.serviceId);
    if (existing) {
        await dispatch(
            updatePetCustomPriceThunk(apiClientWrapper, entry.petId, existing.id, {
                price: entry.price
            })
        );
    } else {
        const payload: PetCustomPriceCreatePayload = {
            serviceId: entry.serviceId,
            price: entry.price
        };
        await dispatch(createPetCustomPriceThunk(apiClientWrapper, entry.petId, payload));
    }
}

/** Creates or updates pet custom prices marked with saveCustomPrice on booking save. */
export async function persistBookingCustomPrices(
    dispatch: Dispatch<any>,
    apiClientWrapper: ApiClientWrapper,
    appointments: Array<AppointmentMultiple>
): Promise<void> {
    const entries = collectPersistEntries(appointments);
    if (entries.length === 0) return;

    const petIds = Array.from(new Set(entries.map(entry => entry.petId)));

    for (const petId of petIds) {
        await dispatch(fetchPetCustomPricesThunk(apiClientWrapper, petId));
        const petEntries = entries.filter(entry => entry.petId === petId);
        const byService = new Map<number, Array<PersistEntry>>();

        for (const entry of petEntries) {
            const serviceEntries = byService.get(entry.serviceId) ?? [];
            serviceEntries.push(entry);
            byService.set(entry.serviceId, serviceEntries);
        }

        for (const [serviceId, serviceEntries] of Array.from(byService.entries())) {
            try {
                const customPrices = store.getState().pets.customPrices || [];
                const pickup = serviceEntries.find(entry => entry.kind === 'pickup');
                const dropoff = serviceEntries.find(entry => entry.kind === 'dropoff');
                const others = serviceEntries.filter(entry => entry.kind !== 'pickup' && entry.kind !== 'dropoff');

                if (pickup && dropoff) {
                    await persistPickupAndDropoff(
                        dispatch,
                        apiClientWrapper,
                        petId,
                        serviceId,
                        pickup.price,
                        dropoff.price,
                        customPrices
                    );
                } else {
                    if (pickup) {
                        await persistSingleCustomPrice(dispatch, apiClientWrapper, pickup, customPrices);
                    }
                    if (dropoff) {
                        await persistSingleCustomPrice(
                            dispatch,
                            apiClientWrapper,
                            dropoff,
                            store.getState().pets.customPrices || []
                        );
                    }
                }

                for (const entry of others) {
                    await persistSingleCustomPrice(
                        dispatch,
                        apiClientWrapper,
                        entry,
                        store.getState().pets.customPrices || []
                    );
                }
            } catch {
                // Booking save should continue even if a custom price persist fails.
            }
        }
    }
}

function dummyPersistDate(): Moment {
    return moment();
}

function standardCustomPriceVariableData(
    saveCustomPrice: boolean | undefined,
    serviceId: number | undefined,
    serviceName: string | undefined,
    price: number,
    customPriceId?: number,
    customPriceAmount?: number
): Array<DataVaraible> {
    if (!saveCustomPrice || !serviceId) return [];

    return [
        {
            date: dummyPersistDate(),
            service: {
                service: { id: serviceId, name: serviceName || '' },
                price,
                durationInMinutes: 0,
                businessArea: '',
                color: '',
                saveCustomPrice: true,
                customPriceId,
                customPriceAmount
            }
        }
    ];
}

export function customPriceDataFromPetService(service: PetServiceWrapper | undefined): Array<DataVaraible> {
    if (!service) return [];
    const formPrice = Number(service.price) || 0;
    const storedAmount = Number(service.customPriceAmount);
    const hasStoredAmount = Number.isFinite(storedAmount) && storedAmount > 0;
    const persistAmount =
        service.enrollment?.id != null && hasStoredAmount ? storedAmount : formPrice || storedAmount || 0;
    return standardCustomPriceVariableData(
        service.saveCustomPrice,
        service.service?.id,
        service.service?.name,
        persistAmount,
        service.customPriceId,
        persistAmount
    );
}

export function customPriceDataFromDaycare(daycare: Daycare): Array<DataVaraible> {
    return standardCustomPriceVariableData(
        daycare.saveCustomPrice,
        daycare.service?.serviceId,
        daycare.service?.serviceName,
        Number(daycare.price) || 0,
        daycare.customPriceId,
        daycare.price
    );
}

export function customPriceDataFromBoarding(boarding: Boarding): Array<DataVaraible> {
    const saveCustomPrice = boarding.service?.selectedOffer == null && boarding.saveCustomPrice;
    return standardCustomPriceVariableData(
        saveCustomPrice,
        boarding.service?.serviceId,
        boarding.service?.serviceName,
        Number(boarding.price) || 0,
        boarding.customPriceId
    );
}

export function customPriceDataFromWalking(walking: Walking): Array<DataVaraible> {
    return standardCustomPriceVariableData(
        walking.saveCustomPrice,
        walking.service?.serviceId,
        walking.service?.serviceName,
        Number(walking.price) || 0,
        walking.customPriceId
    );
}

export function customPriceDataFromTraining(training: Training): Array<DataVaraible> {
    return standardCustomPriceVariableData(
        training.saveCustomPrice,
        training.service?.serviceId,
        training.service?.serviceName,
        Number(training.price) || 0,
        training.customPriceId
    );
}

export function customPriceDataFromTransportation(
    transportation: Transportation,
    fallbackDate: Moment,
    timezone: string
): Array<DataVaraible> {
    return buildTransportationVariableDataPerSlot(transportation, fallbackDate, timezone, false);
}

/** Best-effort persist when adding a service from order detail (Add Pet / Add Order Item). */
export function persistOrderItemCustomPrices(
    dispatch: Dispatch<any>,
    apiClientWrapper: ApiClientWrapper,
    petId: number | undefined,
    variableData: Array<DataVaraible>
): void {
    if (!petId || variableData.length === 0) return;
    void persistBookingCustomPrices(dispatch, apiClientWrapper, [
        {
            clientId: 0,
            petId,
            createdByStaffId: 0,
            variableData
        }
    ]).catch(() => undefined);
}
